import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {ElevationApp} from "../../elevationTool/ElevationApp";
import {EventEmitter2} from "../../elevationTool/util/EventEmitter2";
import {Workspace} from "../../elevationTool/model/view/MdViewSettings";
import {anchorSetupAllowed, endAnchorSetup, startAnchorSetup} from "../../elevationTool/menubar/AnchorSetupTab";

// the sidebar's tabs: the three workspaces plus the frame anchors.
export type SidebarTab = Workspace | "anchors";

export function sidebarTabWorkspace(tab: SidebarTab): Workspace {
    return tab === "anchors" ? "screen" : tab;
}

export function sidebarTabLabel(tab: SidebarTab): string {
    switch(tab) {
        case "screen": return "Screen";
        case "door": return "Door";
        case "hardware": return "Hardware";
        case "anchors": return "Anchors";
    }
}

// put the preview on a tab's view. The screen tab shows the frame grid (the tool's "all"
// overview is too wide for the sidebar); the anchors tab is the frame grid in anchor setup, so
// a tap on a stick shows its anchors.
export function applySidebarTab(app: ElevationApp, tab: SidebarTab) {
    const vs = app.viewSettings;
    if(tab !== "anchors" && vs.anchorSetup.value) endAnchorSetup(app);

    switch(tab) {
        case "screen":
        case "anchors":
            app.setWorkspace("screen");
            if(vs.activeView.value !== "frame-grid") app.showView("frame-grid");
            if(tab === "anchors") {
                vs.showAnchors.emit(true);
                if(!vs.anchorSetup.value && anchorSetupAllowed(app)) startAnchorSetup(app);
            }
            return;
        case "door":
        case "hardware":
            app.setWorkspace(tab);
            return;
    }
}

type LoadProps = Parameters<ElevationApp["load"]>[0];

// a view-only ElevationApp for the openings-grid sidebar. Loads the opening on mount and again
// when it (or its fingerprint) changes; loads run one at a time and a change that lands mid-load
// loads once the running load is done.
export function useSidebarElevationApp(props: {
    projectId: number;
    openingId: number;
    // set in quote mode: the alternative the quote opening belongs to.
    alternative?: number;
    fingerprint: string;
    tab: SidebarTab;
}) {
    const {projectId, openingId, alternative, fingerprint, tab} = props;

    const appRef = useRef<ElevationApp | null>(null);
    const appEmitter = useMemo(() => new EventEmitter2<ElevationApp | null>(null, "elev.sidebar.app"), []);
    const [overlay, setOverlay] = useState<{raw: () => any}>({raw: () => null});

    const tabRef = useRef(tab);
    tabRef.current = tab;

    const pending = useRef<LoadProps | null>(null);
    const loading = useRef(false);

    const runLoads = useCallback(async () => {
        const app = appRef.current;
        if(!app || loading.current) return;

        loading.current = true;
        try {
            while(pending.current) {
                const next = pending.current;
                pending.current = null;
                await app.load(next);
                if(appRef.current !== app) return;
                if(!pending.current && !app.loadError) applySidebarTab(app, tabRef.current);
            }
        } finally {
            loading.current = false;
        }
    }, []);

    const containerRef = useCallback((r: HTMLDivElement | null) => {
        if(appRef.current || !r) return;

        appRef.current = new ElevationApp(r, {
            readOnly: true,
            onShowReact: (el: any) => setOverlay({raw: el}),
            onHideReact: () => setOverlay({raw: () => null}),
        });
        appEmitter.emit(appRef.current);
        runLoads();
    }, [appEmitter, runLoads]);

    useEffect(() => {
        pending.current = {
            projectId,
            opening: String(openingId),
            quoteOpening: alternative !== undefined ? String(openingId) : undefined,
            alternative,
            workspace: sidebarTabWorkspace(tabRef.current),
        };
        runLoads();
        // fingerprint: the opening's elevation links or sizes changed, so load it again.
    }, [projectId, openingId, alternative, fingerprint, runLoads]);

    useEffect(() => {
        const app = appRef.current;
        if(app && app.ready.value && !loading.current && !app.loadError) applySidebarTab(app, tab);
    }, [tab]);

    useEffect(() => {
        return () => {
            setOverlay({raw: () => null});
            appRef.current?.destroy();
            appRef.current = null;
            appEmitter.emit(null);
        };
    }, [appEmitter]);

    return {containerRef, app: appEmitter, overlay};
}
